/* eslint-disable react/jsx-wrap-multilines */
import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Row, Col } from 'reactstrap';
import ModalPopup from 'components/organisms/modal';
import Button from 'components/atoms/button';
import Input from 'components/atoms/input';
import forms from 'constants/forms';
import { showResult, getInitialValues } from 'utils';
import common from 'constants/common';
import { useSelector } from 'react-redux';
import ThresholdsSchema from './schema';

const thresholdsForms = { ...forms.adminPanelsUser.userEdit.thresholds };

const THRESHOLDS_FIELDS = forms.thresholdsFields;

const EditThresholdsModal = ({
  isOpen,
  toggle,
  patientGuid,
  loading,
  thresholds,
  submit,
}) => {
  const handleSubmit = async (values) => {
    const postData = {
      patientGuid,
      ...values,
    };
    const { error } = await submit(postData);
    showResult(error, common.submitSuccessTypes.changed, toggle);
  };

  const patientDevices = useSelector((state) => state.patient.devices);
  const isBloodGlucoseType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'BloodGlucose'),
  );
  const isWeightType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'Weight'),
  );
  const isSPO2Type = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'SpO2'),
  );
  const isBloodPressureType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'BloodPressure'),
  );
  const isSleepMatType = patientDevices.some((device) =>
    device.readingTypes.some(
      (type) =>
        type === 'SleepingMatDuration' || type === 'SleepingMatActivity',
    ),
  );
  const isPeakFlowType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'PeakFlow'),
  );

  const isThresholdDevice =
    isBloodGlucoseType ||
    isWeightType ||
    isSPO2Type ||
    isBloodPressureType ||
    isSleepMatType ||
    isPeakFlowType;

  return (
    <ModalPopup
      isOpen={isOpen}
      toggle={toggle}
      title="Edit Thresholds Info"
      className="popup thresholds-popup"
    >
      <Formik
        initialValues={getInitialValues(THRESHOLDS_FIELDS, thresholds)}
        onSubmit={handleSubmit}
        validationSchema={ThresholdsSchema}
      >
        {({ errors }) => {
          const systolic = (
            <>
              <h5>Systolic</h5>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxCriticalSystolic"
                      name="maxCriticalSystolic"
                      label="Max Critical"
                    />
                    {errors.maxCriticalSystolic ? (
                      <div className="input-error">
                        {errors.maxCriticalSystolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minCriticalSystolic"
                      name="minCriticalSystolic"
                      label="Min Critical"
                    />
                    {errors.minCriticalSystolic ? (
                      <div className="input-error">
                        {errors.minCriticalSystolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxRiskSystolic"
                      name="maxRiskSystolic"
                      label="Max at Risk"
                    />
                    {errors.maxRiskSystolic ? (
                      <div className="input-error">
                        {errors.maxRiskSystolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minRiskSystolic"
                      name="minRiskSystolic"
                      label="Min at Risk"
                    />
                    {errors.minRiskSystolic ? (
                      <div className="input-error">
                        {errors.minRiskSystolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </>
          );
          const diastolic = (
            <>
              <h5>Diastolic</h5>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxCriticalDiastolic"
                      name="maxCriticalDiastolic"
                      label="Max Critical"
                    />
                    {errors.maxCriticalDiastolic ? (
                      <div className="input-error">
                        {errors.maxCriticalDiastolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minCriticalDiastolic"
                      name="minCriticalDiastolic"
                      label="Min Critical"
                    />
                    {errors.minCriticalDiastolic ? (
                      <div className="input-error">
                        {errors.minCriticalDiastolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxRiskDiastolic"
                      name="maxRiskDiastolic"
                      label="Max at Risk"
                    />
                    {errors.maxRiskDiastolic ? (
                      <div className="input-error">
                        {errors.maxRiskDiastolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minRiskDiastolic"
                      name="minRiskDiastolic"
                      label="Min at Risk"
                    />
                    {errors.minRiskDiastolic ? (
                      <div className="input-error">
                        {errors.minRiskDiastolic}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </>
          );
          const pulse = (
            <>
              <h5>Pulse</h5>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxCriticalPulse"
                      name="maxCriticalPulse"
                      label="Max Critical"
                    />
                    {errors.maxCriticalPulse ? (
                      <div className="input-error">
                        {errors.maxCriticalPulse}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minCriticalPulse"
                      name="minCriticalPulse"
                      label="Min Critical"
                    />
                    {errors.minCriticalPulse ? (
                      <div className="input-error">
                        {errors.minCriticalPulse}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxRiskPulse"
                      name="maxRiskPulse"
                      label="Max at Risk"
                    />
                    {errors.maxRiskPulse ? (
                      <div className="input-error">{errors.maxRiskPulse}</div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minRiskPulse"
                      name="minRiskPulse"
                      label="Min at Risk"
                    />
                    {errors.minRiskPulse ? (
                      <div className="input-error">{errors.minRiskPulse}</div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </>
          );
          const sleepPulse = (
            <>
              <h5>Resting Pulse</h5>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxCriticalSleepPulse"
                      name="maxCriticalSleepPulse"
                      label="Max Critical"
                    />
                    {errors.maxCriticalSleepPulse ? (
                      <div className="input-error">
                        {errors.maxCriticalSleepPulse}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minCriticalSleepPulse"
                      name="minCriticalSleepPulse"
                      label="Min Critical"
                    />
                    {errors.minCriticalSleepPulse ? (
                      <div className="input-error">
                        {errors.minCriticalSleepPulse}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxRiskSleepPulse"
                      name="maxRiskSleepPulse"
                      label="Max at Risk"
                    />
                    {errors.maxRiskSleepPulse ? (
                      <div className="input-error">
                        {errors.maxRiskSleepPulse}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minRiskSleepPulse"
                      name="minRiskSleepPulse"
                      label="Min at Risk"
                    />
                    {errors.minRiskSleepPulse ? (
                      <div className="input-error">
                        {errors.minRiskSleepPulse}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </>
          );
          const spo2 = (
            <>
              <h5>Sp02</h5>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="criticalBloodoxygen"
                      name="criticalBloodoxygen"
                      label="Critical"
                    />
                    {errors.criticalBloodoxygen ? (
                      <div className="input-error">
                        {errors.criticalBloodoxygen}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="riskBloodoxygen"
                      name="riskBloodoxygen"
                      label="At Risk"
                    />
                    {errors.riskBloodoxygen ? (
                      <div className="input-error">
                        {errors.riskBloodoxygen}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </>
          );
          const glucose = (
            <>
              <h5>Glucose</h5>
              <Row>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxCriticalGlucose"
                      name="maxCriticalGlucose"
                      label="Max Critical"
                    />
                    {errors.maxCriticalGlucose ? (
                      <div className="input-error">
                        {errors.maxCriticalGlucose}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minCriticalGlucose"
                      name="minCriticalGlucose"
                      label="Min Critical"
                    />
                    {errors.minCriticalGlucose ? (
                      <div className="input-error">
                        {errors.minCriticalGlucose}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxRiskGlucose"
                      name="maxRiskGlucose"
                      label="Max at Risk"
                    />
                    {errors.maxRiskGlucose ? (
                      <div className="input-error">{errors.maxRiskGlucose}</div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minRiskGlucose"
                      name="minRiskGlucose"
                      label="Min at Risk"
                    />
                    {errors.minRiskGlucose ? (
                      <div className="input-error">{errors.minRiskGlucose}</div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </>
          );
          const weight = (
            <>
              <h5>Weight</h5>
              <Row>
                <Col md="4" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="baseLineWeight"
                      name="baseLineWeight"
                      label={
                        <>
                          <span>Baseline</span>
                          <span>lbs</span>
                        </>
                      }
                      type="text"
                    />
                    {errors.baseLineWeight ? (
                      <div className="input-error">{errors.baseLineWeight}</div>
                    ) : null}
                  </div>
                </Col>
                <Col md="4" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxCriticalWeight"
                      name="maxCriticalWeight"
                      label="Max Critical"
                    />
                    {errors.maxCriticalWeight ? (
                      <div className="input-error">
                        {errors.maxCriticalWeight}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="4" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minCriticalWeight"
                      name="minCriticalWeight"
                      label="Min Critical"
                    />
                    {errors.minCriticalWeight ? (
                      <div className="input-error">
                        {errors.minCriticalWeight}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="4" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="maxRiskWeight"
                      name="maxRiskWeight"
                      label="Max at Risk"
                    />
                    {errors.maxRiskWeight ? (
                      <div className="input-error">{errors.maxRiskWeight}</div>
                    ) : null}
                  </div>
                </Col>
                <Col md="4" xs="12">
                  <div className="thresholds-popup-input">
                    <Input
                      id="minRiskWeight"
                      name="minRiskWeight"
                      label="Min at Risk"
                    />
                    {errors.minRiskWeight ? (
                      <div className="input-error">{errors.minRiskWeight}</div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </>
          );

          const pef = (
            <>
              <h5>PEF</h5>
              <Row>
                {thresholdsForms.pef.map((field) => (
                  <Col lg="6" md="6" sm="6" xs="12" key={field.id}>
                    <Input {...field} />
                    {errors[field.id] ? (
                      <div className="input-error">{errors[field.id]}</div>
                    ) : null}
                  </Col>
                ))}
              </Row>
            </>
          );

          const fev1 = (
            <>
              <h5>FEV1</h5>
              <Row>
                {thresholdsForms.fev1.map((field) => (
                  <Col lg="6" md="6" sm="6" xs="12" key={field.id}>
                    <Input {...field} />
                    {errors[field.id] ? (
                      <div className="input-error">{errors[field.id]}</div>
                    ) : null}
                  </Col>
                ))}
              </Row>
            </>
          );

          const thresholdInputs = [];
          if (isBloodPressureType) {
            thresholdInputs.push(systolic);
            thresholdInputs.push(diastolic);
          }
          if (isSPO2Type || isBloodPressureType) {
            thresholdInputs.push(pulse);
          }
          if (isSleepMatType) {
            thresholdInputs.push(sleepPulse);
          }
          if (isBloodGlucoseType) {
            thresholdInputs.push(glucose);
          }
          if (isWeightType) {
            thresholdInputs.push(weight);
          }
          if (isPeakFlowType) {
            thresholdInputs.push(pef);
            thresholdInputs.push(fev1);
          }
          if (isSPO2Type) {
            thresholdInputs.push(spo2);
          }

          return (
            <Form className="user--form">
              {isThresholdDevice ? (
                <Row>
                  {thresholdInputs.map((_, index) => (
                    <Col md="4" sm="6" xs="12">
                      {thresholdInputs[index]}
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="input-error">
                  <h6>
                    Patient does not have any device or device does not match
                    thresholds data.
                  </h6>
                </div>
              )}
              {isThresholdDevice && (
                <div className="text-center">
                  <Button type="submit" text="Save" disabled={loading} />
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </ModalPopup>
  );
};
EditThresholdsModal.defaultProps = {
  isOpen: false,
  thresholds: {},
};

EditThresholdsModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  patientGuid: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  thresholds: PropTypes.shape(),
};

export default EditThresholdsModal;
